import {Injectable} from "@angular/core";
import {NavigationEnd, Router, UrlTree} from "@angular/router";
import {Location} from "@angular/common";
import {combineLatest} from "rxjs";
import {filter, map} from "rxjs/operators";
import {UserService} from "../common/user.service";
import {IShopUser} from "../session/customer-models";
import {ShareableUtil} from "../social/shareable-util";
import {SponsorService} from "../common/sponsor.service";
import {ISponsorDetails} from "../social/social-models";
import {AuthUtil} from "../auth/auth-util";

@Injectable({
    providedIn: 'root'
})
export class ReferralUrlService {

    constructor(
        private readonly router: Router,
        private readonly location: Location,
        private readonly userService: UserService,
        private readonly sponsorService: SponsorService
    ) {}

    init(): void {
        const navigationEnd$ = this.router.events.pipe(
            filter(event => event instanceof NavigationEnd),
            map(event => event)
        );
        const shopUser$ = this.userService.watchShopUser().pipe(
            filter((user: IShopUser) => !!user)
        );

        combineLatest([navigationEnd$, shopUser$, this.sponsorService.sponsorDetailsSubject.watch()]).subscribe(
            ([event, user, sponsor]: [NavigationEnd, IShopUser, ISponsorDetails]) => {
                const urlTree: UrlTree = this.router.parseUrl(event.urlAfterRedirects);
                const refCode: string = this.resolveRefCode(user, sponsor);

                if (!refCode) {
                    if (urlTree.queryParams['ref']) {
                        const {ref, ...remainingParams} = urlTree.queryParams;
                        urlTree.queryParams = remainingParams;
                        this.location.replaceState(this.router.serializeUrl(urlTree));
                    }
                    return;
                }

                if (urlTree.queryParams['ref'] === refCode) {
                    return;
                }

                urlTree.queryParams = {...urlTree.queryParams, ref: refCode};
                this.location.replaceState(this.router.serializeUrl(urlTree));
            }
        );
    }

    /**
     * The code the url should advertise, or null to carry none. A shopper who can sponsor advertises
     * their own site, so anything they share carries their code. A retail shopper instead carries
     * whichever sponsor was actually resolved for the visit
     */
    private resolveRefCode(user: IShopUser, sponsor: ISponsorDetails): string {
        if (!!user?.token && ShareableUtil.canSponsor(user)) {
            return user.siteSubdomain || (user.customerId ? String(user.customerId) : null);
        }
        if (AuthUtil.isRetail(user) && !!sponsor?.sponsorId) {
            return sponsor.site || String(sponsor.sponsorId);
        }
        return null;
    }
}
